import type { Degree, Distance, Margins, SpatialRectangle } from "@pdfking/math"
import {
  Abs,
  AddMargins,
  Floor,
  Hypot,
  Max,
  Min,
  RemoveMargins,
  Scale,
  Sqrt,
  Subtract,
  ToMargins,
  Total,
} from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { ToPixel } from "@pdfking/quantity"
import type { ColorID, PageShape } from "@pdfking/renderer-api"
import {
  ArcPoints,
  ClipShapes,
  GroupMatrix,
  HorizontalAlign,
  Oval,
  PlotFunction,
  VerticalAlign,
} from "@pdfking/renderer-api"
import { NullQ, NumberQ } from "@pdfking/structs"

import { PALETTE, POSTER_CITIES } from "#constants"
import type { Measure, RandomStream } from "#internal"

import { RandomPick } from "../random/RandomPick"

// A Bauhaus poster in `box`, split in `panels` side by side pages — so many
// equal ones, or each its own width, the lot adding up to the box's —: each
// page its own bold ground and a small shape, one big shape and one bar
// running across them all, thin lines, `word` huge and turned on page
// `word_panel`, a small label on every other one, so no page is ever bare.
// Only page `page` is drawn, and as far as its `bleed` reaches: its own
// ground, lines and text run on into it, the big shape and the bar across
// from the next page. Its text, the word and the labels, keeps within each
// page's `security`. The whole poster is laid out every time, so facing
// pages always match. Every page has a disc of the spot ink of its own,
// under the small shapes, the lines and the text, which knock it out
export const DrawPoster = ({
  bleed = ToMargins(ZERO_DISTANCE),
  box,
  measure,
  page = 0,
  panels,
  random,
  security = ToMargins(ZERO_DISTANCE),
  word,
  word_panel,
}: {
  bleed?: Margins<Distance>
  box: SpatialRectangle<Distance>
  measure: Measure
  page?: number
  panels: number | Distance[]
  random: RandomStream
  security?: Margins<Distance>
  word: string
  word_panel: number
}): PageShape[] => {
  const { height, width } = box
  // Where each page starts and how wide it is
  const widths = NumberQ(panels) ? [...Array(panels)].map(() => Scale(width, 1 / panels)) : panels
  const starts = widths.map((_, i) => Total(ZERO_DISTANCE, ...widths.slice(0, i)))
  const short = Min(...widths, height)
  // Where a page's text may go: the page within its security
  const safe = (panel: number): SpatialRectangle<Distance> =>
    RemoveMargins({ height, width: widths[panel]!, x: starts[panel]!, y: ZERO_DISTANCE }, security)
  // One ink may be a ground, the other is the shape's and never one
  const [accent, ink] = RandomPick(random, [
    [PALETTE.primary, PALETTE.secondary],
    [PALETTE.secondary, PALETTE.primary],
  ])
  const grounds = widths.map(() =>
    RandomPick(random, [PALETTE.paper, PALETTE.paper, PALETTE.text, accent])
  )
  const contrast = (ground: ColorID) => (ground === PALETTE.paper ? PALETTE.text : PALETTE.paper)
  const ground = grounds[page]!
  // The page and its bleed, all that is drawn, in the poster's own frame
  const page_box = AddMargins(
    { height, width: widths[page]!, x: starts[page]!, y: ZERO_DISTANCE },
    bleed
  )
  const shapes: PageShape[] = [
    { enum: PlotFunction.PLOT_RECT, fill_color: ground, position: page_box },
  ]

  // The shape: a circle, half one, a square or a quarter filling a corner,
  // anywhere along the whole width, the gutter included
  const center = {
    x: Scale(width, RandomPick(random, [0.25, 1 / 3, 0.5, 2 / 3, 0.75])),
    y: Scale(height, RandomPick(random, [1 / 3, 0.5, 2 / 3])),
  }
  const radius = Scale(short, 0.24 + random() * 0.16)
  const turn = (Floor(random() * 4) * 90) as Degree
  switch (RandomPick(random, ["circle", "half", "square", "corner"] as const)) {
    case "circle":
      shapes.push({
        closed: true,
        enum: PlotFunction.PLOT_PATH,
        fill_color: ink,
        points: Oval(center, radius),
      })
      break
    case "half":
      shapes.push({
        closed: true,
        enum: PlotFunction.PLOT_PATH,
        fill_color: ink,
        points: ArcPoints(center, Scale(radius, 1.3), turn, 180 as Degree),
      })
      break
    case "square":
      shapes.push({
        enum: PlotFunction.PLOT_GROUP,
        matrix: GroupMatrix({ rotation: RandomPick(random, [0, 45]) as Degree, translate: center }),
        shapes: [
          {
            enum: PlotFunction.PLOT_RECT,
            fill_color: ink,
            position: {
              height: Scale(radius, 2),
              width: Scale(radius, 2),
              x: Scale(radius, -1),
              y: Scale(radius, -1),
            },
          },
        ],
      })
      break
    case "corner": {
      const corner = {
        x: RandomPick(random, [ZERO_DISTANCE, width]),
        y: RandomPick(random, [ZERO_DISTANCE, height]),
      }
      shapes.push({
        closed: true,
        enum: PlotFunction.PLOT_PATH,
        fill_color: ink,
        points: Oval(corner, Scale(short, 0.62)),
      })
    }
  }

  // The bar: across every page at a third of the height, or down one of them
  const thickness = Scale(short, 0.04 + random() * 0.06)
  const bar = RandomPick(random, [accent, PALETTE.text])
  shapes.push({
    enum: PlotFunction.PLOT_RECT,
    fill_color: bar,
    position:
      random() < 0.5
        ? {
            height: thickness,
            width: Total(width, bleed.left, bleed.right),
            x: Scale(bleed.left, -1),
            y: Subtract(Scale(height, RandomPick(random, [1 / 3, 2 / 3])), Scale(thickness, 0.5)),
          }
        : {
            height: Total(height, bleed.top, bleed.bottom),
            width: thickness,
            x: Subtract(
              Scale(width, RandomPick(random, [1 / 6, 1 / 3, 2 / 3, 5 / 6])),
              Scale(thickness, 0.5)
            ),
            y: Scale(bleed.bottom, -1),
          },
  })

  // Where the text goes, worked out first so the small shapes keep clear of
  // it. The word, as big as its page lets it, turned: up the page, down it,
  // level, or rising at 45°
  const rotation = RandomPick(random, [90, 90, -90, 0, 45])
  const room = safe(word_panel)
  const word_width = room.width
  const span =
    rotation === 0
      ? word_width
      : rotation === 45
        ? Scale(word_width, 0.8 * Hypot(1, room.height / word_width))
        : room.height
  const across = rotation === 0 ? room.height : word_width
  const word_size = Min(Scale(short, (0.88 * span) / measure(word, short)), Scale(across, 0.45))
  // Along an edge, a margin away from it
  const edge = (extent: Distance) =>
    RandomPick(random, [
      Total(Scale(short, 0.05), Scale(word_size, 0.5)),
      Subtract(extent, Total(Scale(short, 0.05), Scale(word_size, 0.5))),
    ])
  const word_center = {
    x: Total(room.x, rotation === 0 || rotation === 45 ? Scale(word_width, 0.5) : edge(word_width)),
    y: Total(room.y, rotation === 0 ? edge(room.height) : Scale(room.height, 0.5)),
  }
  const word_length = measure(word, word_size)
  const half =
    rotation === 0
      ? { x: Scale(word_length, 0.5), y: Scale(word_size, 0.5) }
      : rotation === 45
        ? {
            x: Scale(Total(word_length, word_size), 1 / (2 * Sqrt(2))),
            y: Scale(Total(word_length, word_size), 1 / (2 * Sqrt(2))),
          }
        : { x: Scale(word_size, 0.5), y: Scale(word_length, 0.5) }

  // The label, small in a corner of every page but the word's, which has its
  // text already and would run it over the word: its lines, bottom up, and
  // the box they take
  const label = Scale(short, 0.03)
  const label_margin = Scale(short, 0.06)
  const labels = [...widths.keys()]
    .filter(panel => panel !== word_panel)
    .map(panel => {
      const right = random() < 0.5
      const room = safe(panel)
      const bottom = Total(room.y, label_margin)
      const lines = [
        [RandomPick(random, POSTER_CITIES), Total(bottom, Scale(label, 1.3))],
        [`${1900 + Floor(random() * 41)}`, bottom],
      ] as const
      const label_width = Max(...lines.map(([value]) => measure(value, label)))
      const x = right
        ? Subtract(Total(room.x, room.width), label_margin)
        : Total(room.x, label_margin)
      const box: SpatialRectangle<Distance> = {
        height: Scale(label, 2.3),
        width: label_width,
        x: right ? Subtract(x, label_width) : x,
        y: bottom,
      }
      return { box, lines, panel, right }
    })

  const text_boxes = [
    {
      box: {
        height: Scale(half.y, 2),
        width: Scale(half.x, 2),
        x: Subtract(word_center.x, half.x),
        y: Subtract(word_center.y, half.y),
      },
      panel: word_panel,
    },
    ...labels.map(({ box, panel }) => ({ box, panel })),
  ]

  // The spot's disc on every page, somewhere in it: laid out for all of
  // them, drawn on this one
  grounds.forEach((_, panel) => {
    const disc = {
      x: Total(starts[panel]!, Scale(widths[panel]!, RandomPick(random, [0.3, 0.5, 0.7]))),
      y: Scale(height, RandomPick(random, [0.25, 0.5, 0.75])),
    }
    const r = Scale(short, 0.1 + random() * 0.08)
    if (panel === page)
      shapes.push({
        closed: true,
        enum: PlotFunction.PLOT_PATH,
        fill_color: PALETTE.spot,
        points: Oval(disc, r),
      })
  })

  // A small shape on every page, a third of the way in from a corner, in
  // the shape's ink. Where it would touch the text it moves left or right
  // just clear of it, inside its page, or when it cannot, it stays out
  const gap = Scale(short, 0.02)
  grounds.forEach((_, panel) => {
    const size = Scale(short, 0.06 + random() * 0.06)
    const y = Scale(height, RandomPick(random, [0.2, 0.8]))
    const left = Total(starts[panel]!, size)
    const right = Subtract(Total(starts[panel]!, widths[panel]!), size)
    const boxes = text_boxes.filter(text => text.panel === panel).map(({ box }) => box)
    const reach = Total(size, gap)
    const touches = (x: Distance) =>
      boxes.some(
        box =>
          Total(x, reach) > box.x &&
          Subtract(x, reach) < Total(box.x, box.width) &&
          Total(y, reach) > box.y &&
          Subtract(y, reach) < Total(box.y, box.height)
      )
    const start = Total(starts[panel]!, Scale(widths[panel]!, RandomPick(random, [0.25, 0.75])))
    const x = [
      start,
      ...boxes
        .flatMap(box => [Subtract(box.x, reach), Total(box.x, box.width, reach)])
        .sort((a, b) => Abs(a - start) - Abs(b - start)),
    ].find(x => x >= left && x <= right && !touches(x))
    const round = random() < 0.5
    if (NullQ(x) || panel !== page) return
    shapes.push(
      round
        ? {
            closed: true,
            enum: PlotFunction.PLOT_PATH,
            fill_color: ink,
            points: Oval({ x, y }, size),
          }
        : {
            enum: PlotFunction.PLOT_RECT,
            fill_color: ink,
            position: {
              height: Scale(size, 2),
              width: Scale(size, 2),
              x: Subtract(x, size),
              y: Subtract(y, size),
            },
          }
    )
  })

  // Thin parallel lines through a point, in the page's contrast
  const lines = 3 + Floor(random() * 5)
  const spacing = Scale(short, 0.025)
  const through = { x: Scale(width, random()), y: Scale(height, random()) }
  const line_angle = RandomPick(random, [0, 90, 45, -45]) as Degree
  // Past every corner of the poster, whatever the turn
  const reach = Total(width, height)
  const line_width = Scale(short, 0.005)
  shapes.push({
    enum: PlotFunction.PLOT_GROUP,
    matrix: GroupMatrix({ rotation: line_angle, translate: through }),
    shapes: Array.from({ length: lines }, (_, i) => ({
      enum: PlotFunction.PLOT_RECT,
      fill_color: contrast(ground),
      position: {
        height: line_width,
        width: Scale(reach, 2),
        x: Scale(reach, -1),
        y: Subtract(Scale(spacing, i), Scale(line_width, 0.5)),
      },
    })),
  })

  // The text, over everything
  const texts: PageShape[] = []
  if (word_panel === page)
    texts.push({
      enum: PlotFunction.PLOT_GROUP,
      matrix: GroupMatrix({ rotation: rotation as Degree, translate: word_center }),
      shapes: [
        {
          enum: PlotFunction.PLOT_TEXT,
          font_size: ToPixel(word_size),
          horizontal_align: HorizontalAlign.CENTER,
          position: {
            height: word_size,
            width: word_length,
            x: Scale(word_length, -0.5),
            y: Scale(word_size, -0.5),
          },
          text: word,
          text_color: contrast(ground),
          vertical_align: VerticalAlign.CENTER,
        },
      ],
    })
  for (const { box: label_box, lines, panel, right } of labels)
    if (panel === page)
      for (const [value, y] of lines)
        texts.push({
          enum: PlotFunction.PLOT_TEXT,
          font_size: ToPixel(label),
          horizontal_align: right ? HorizontalAlign.RIGHT : HorizontalAlign.LEFT,
          position: { height: label, width: label_box.width, x: label_box.x, y },
          text: value,
          text_color: contrast(ground),
          vertical_align: VerticalAlign.BOTTOM,
        })

  return [
    {
      enum: PlotFunction.PLOT_GROUP,
      matrix: GroupMatrix({ translate: { x: box.x, y: box.y } }),
      shapes: [...ClipShapes({ box: page_box, shapes }), ...texts],
    },
  ]
}
